import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  clearCachedUserAccess,
  getCachedUserAccessSnapshot,
  getUserAccess,
} from "../utils/authRoles";
import { traceAsync } from "../utils/perfTrace";

export default function AdminRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingAttemptsRef = useRef(0);
  const redirect = `${location.pathname}${location.search}`;
  const loginPath = `/login?redirect=${encodeURIComponent(redirect)}`;
  const [state, setState] = useState(() => {
    const cachedAccess = getCachedUserAccessSnapshot();
    return {
      loading: !cachedAccess,
      allowed: Boolean(cachedAccess?.isAdmin),
      signedIn: Boolean(cachedAccess?.isAdmin || cachedAccess?.isBroker),
    };
  });

  useEffect(() => {
    let mounted = true;
    let checkId = 0;
    let retryTimer = null;

    const checkAccess = async () => {
      const currentCheck = ++checkId;
      const access = await traceAsync("route.admin.access", () => getUserAccess(), {
        attempt: pendingAttemptsRef.current + 1,
      });
      if (!mounted || currentCheck !== checkId) return;

      if (access.accessPending) {
        pendingAttemptsRef.current += 1;
        if (pendingAttemptsRef.current >= 8) {
          clearCachedUserAccess();
          setState({ loading: false, allowed: false, signedIn: false });
          navigate(loginPath, { replace: true });
          return;
        }

        setState((previous) => {
          if (previous.signedIn) return previous;
          return { loading: true, allowed: false, signedIn: true };
        });
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = setTimeout(checkAccess, 1000);
        return;
      }

      pendingAttemptsRef.current = 0;
      setState({
        loading: false,
        allowed: access.isAdmin,
        signedIn: !!access.user,
      });

      if (!access.user) {
        navigate(loginPath, { replace: true });
      }
    };

    checkAccess();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === "SIGNED_OUT") {
        checkId += 1;
        clearCachedUserAccess();
        setState({ loading: false, allowed: false, signedIn: false });
        navigate(loginPath, { replace: true });
        return;
      }

      if (!session) {
        return;
      }

      await checkAccess();
    });

    return () => {
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
      authListener?.subscription?.unsubscribe?.();
    };
  }, [loginPath, navigate]);

  if (state.loading) {
    return null;
  }

  if (!state.signedIn) return <Navigate to={loginPath} replace />;
  if (!state.allowed) return <Navigate to="/portal" replace />;

  return children;
}
